import { memo, useMemo } from 'react';
import { cn } from '@cyphex/ui';
import { normaliseWaveform } from '../../lib/waveform';

interface WaveformProps {
  waveform: readonly number[];
  /** 0..1 — bars before this point render in the played colour. */
  progress?: number;
  onSeek?: (fraction: number) => void;
  className?: string;
}

const MIN_BAR_PERCENT = 12;

/** 64 CSS bars — no canvas, no animation frames, no repaint while idle. */
export const Waveform = memo(function Waveform({ waveform, progress = 0, onSeek, className }: WaveformProps) {
  const bars = useMemo(() => normaliseWaveform(waveform), [waveform]);
  const playedIndex = Math.round(progress * bars.length);

  return (
    <div
      className={cn('flex h-8 flex-1 items-center gap-[2px]', onSeek && 'cursor-pointer', className)}
      onClick={
        onSeek
          ? (event) => {
              const rect = event.currentTarget.getBoundingClientRect();
              onSeek(Math.min(1, Math.max(0, (event.clientX - rect.left) / rect.width)));
            }
          : undefined
      }
      role={onSeek ? 'slider' : 'img'}
      aria-label="Voice message waveform"
      aria-valuenow={onSeek ? Math.round(progress * 100) : undefined}
      aria-valuemin={onSeek ? 0 : undefined}
      aria-valuemax={onSeek ? 100 : undefined}
      tabIndex={onSeek ? 0 : undefined}
    >
      {bars.map((value, index) => (
        <span
          key={index}
          style={{ height: `${Math.max(MIN_BAR_PERCENT, value)}%` }}
          className={cn(
            'w-full min-w-[2px] rounded-pill transition-colors duration-fast',
            index < playedIndex ? 'bg-primary' : 'bg-border-strong',
          )}
        />
      ))}
    </div>
  );
});
